import React, { useCallback, useState } from 'react'

import _ from 'lodash'

import { ADDITIONAL_RESULT_ATTRIBUTE_KEYS } from '../constants'
import { isNullOrUndefined, selectMostRecentUpdatedTimestamp } from '../utils'

import ResultFooter from './components/ResultFooter'
import ResultText from './components/ResultText'
import ResultTitle from './components/ResultTitle'
import SingleLeftArrow from './components/SingleLeftArrow'
import SingleRightArrow from './components/SingleRightArrow'


const KENDRA_SUGGESTED_ANSWERS = 'Amazon Kendra suggested answers'
const MAX_TOP_ANSWER_LENGTH = 25

const TopResults = ({ results, submitFeedback }) => {
  // All results in this component has QueryResultType === "ANSWER"
  const [currentResultIndex, setCurrentResultIndex] = useState(0)
  const totalResults = results.length

  const updatePreviousResultIndex = useCallback(() => {
    if (currentResultIndex > 0) {
      setCurrentResultIndex(prevIndex => prevIndex - 1)
    }
  }, [currentResultIndex])

  const updateNextResultIndex = useCallback(() => {
    if (currentResultIndex < totalResults - 1) {
      setCurrentResultIndex(prevIndex => prevIndex + 1)
    }
  }, [currentResultIndex, totalResults])

  const getTopAnswer = useCallback(text => {
    if (text && text.Highlights) {
      for (const highlight of text.Highlights) {
        const length = highlight.EndOffset - highlight.BeginOffset
        if (
          highlight &&
          highlight.TopAnswer &&
          length < MAX_TOP_ANSWER_LENGTH
        ) {
          return text.Text?.substring(
            highlight.BeginOffset,
            highlight.EndOffset
          )
        }
      }
    }
    return null
  }, [])

  const renderResults = useCallback(
    (result, idx) => {
      if (!isNullOrUndefined(result)) {
        let attributes = {}
        if (!isNullOrUndefined(result.DocumentAttributes)) {
          result.DocumentAttributes.forEach(attribute => {
            attributes[attribute.Key] = attribute.Value
          })
        }

        let resultAttributes = {}
        if (!isNullOrUndefined(result.AdditionalAttributes)) {
          result.AdditionalAttributes.forEach(attribute => {
            resultAttributes[attribute.Key] = attribute.Value
          })
        }

        const answer =
          resultAttributes[ADDITIONAL_RESULT_ATTRIBUTE_KEYS.ANSWER_TEXT]
        const lastUpdated = selectMostRecentUpdatedTimestamp(attributes)
        const topAnswer = getTopAnswer(answer.TextWithHighlightsValue)

        return (
          <React.Fragment key={result.Id}>
            <div className="container-body">
              <ResultTitle
                queryResultItem={result}
                attributes={attributes}
                submitFeedback={submitFeedback}
              />
              {!_.isEmpty(topAnswer) && <h1>{topAnswer}</h1>}
              <ResultText
                isTopResult={true}
                text={answer.TextWithHighlightsValue}
                lastUpdated={lastUpdated}
              />
              <ResultFooter
                queryResultItem={result}
                attributes={attributes}
                submitFeedback={submitFeedback}
              />
            </div>
          </React.Fragment>
        )
      }
      return null
    },
    [getTopAnswer, submitFeedback]
  )

  if (isNullOrUndefined(results) || results.length === 0) {
    return null
  }

  const resultsToShow = results.map(renderResults)

  return (
    <div className="result-container card">
      <div className="card-title">{KENDRA_SUGGESTED_ANSWERS}</div>
      <div className="container-divider" />
      <div className="carousel-relative-wrapper inside-card-result-container">
        <div className="carousel-wrapper">
          <div className="carousel-container">
            <div className="result-item">
              {resultsToShow[currentResultIndex]}
            </div>
            {resultsToShow.map((res, idx) => (
              <div className="offscreen result-item" key={idx}>
                {res}
              </div>
            ))}
          </div>
        </div>

        {currentResultIndex > 0 && (
          <div className="arrow-left">
            <div className="arrow" onClick={updatePreviousResultIndex}>
              <SingleLeftArrow />
            </div>
          </div>
        )}

        {currentResultIndex < totalResults - 1 && (
          <div className="arrow-right">
            <div className="arrow" onClick={updateNextResultIndex}>
              <SingleRightArrow />
            </div>
          </div>
        )}
      </div>

      {totalResults > 1 && (
        <ul className="kendra-carousel-indicators">
          {[...Array(totalResults)].map((x, index) => (
            <li
              key={index}
              className={
                index === currentResultIndex
                  ? 'kendra-carousel-indicator kendra-carousel-indicator--active'
                  : 'kendra-carousel-indicator'
              }
            />
          ))}
        </ul>
      )}
    </div>
  )
}

export default TopResults
